import { AxiosResponse } from 'axios';
import {
  AccountBasicChangeType,
  UserRegisterInfoType
} from '../../validation/user/userType';
import { csrfToken, getCsrfToken, instance, getCsrfTokenOnCookie } from '../db';
import { UseFormWatch } from 'react-hook-form';
import { ContactType } from '../../validation/contact/contactType';
import { contactKind } from '../../constants/Enum';

// ログインAPI
export const userLogin = async (
  email: string,
  password: string
): Promise<AxiosResponse> => {
  const csrftoken = await getCsrfTokenOnCookie();

  const response = await instance.post(
    '/login',
    { email: email, password: password },
    {
      headers: {
        'X-XSRF-TOKEN': csrftoken
      }
    }
  );

  localStorage.setItem('user_type', response.data.type);
  localStorage.setItem('co2_status', response.data.co2_status);
  return response;
};

// 認証メール送信API
export const sendMailForResisterUser = async (
  email: string
): Promise<AxiosResponse> => {
  const csrftoken = await getCsrfTokenOnCookie();

  return await instance.post(
    '/users/email',
    { email: email },
    {
      headers: {
        'X-XSRF-TOKEN': csrftoken
      }
    }
  );
};

// メール認証API
export const verifyEmail = async (confirmationToken: string | null) => {
  return await instance.get(
    '/users/email_verification?token=' + confirmationToken
  );
};

// 会員情報登録API
export const registerUserInfo = async (
  id: string,
  email: string,
  token: string,
  watch: (fieldName: string) => UserRegisterInfoType
): Promise<AxiosResponse> => {
  const csrftoken = await getCsrfTokenOnCookie();

  return await instance.post(
    '/users/register',
    {
      id,
      email,
      token,
      company: watch('company'),
      company_kana: watch('companyKana'),
      company_short: watch('companyShort'),
      department: watch('department'),
      last_name: watch('lastName'),
      first_name: watch('firstName'),
      last_name_kana: watch('lastNameKana'),
      first_name_kana: watch('firstNameKana'),
      tel: watch('tel'),
      password: watch('password'),
      homepage: watch('homepage'),
      industry: watch('industry')
    },
    {
      headers: {
        'X-XSRF-TOKEN': csrftoken
      }
    }
  );
};

// パスワードリセットAPI
export const sendMailForResetPassword = async (
  email: string
): Promise<AxiosResponse> => {
  const csrftoken = await getCsrfTokenOnCookie();

  return await instance.put(
    '/users/reset_password_email',
    { email: email },
    {
      headers: {
        'X-XSRF-TOKEN': csrftoken
      }
    }
  );
};

// パスワード再設定
export const resetPassword = async (
  password: string,
  token: string
): Promise<AxiosResponse> => {
  const csrftoken = await getCsrfTokenOnCookie();

  return await instance.put(
    '/users/password',
    { password: password, token: token },
    {
      headers: {
        'X-XSRF-TOKEN': csrftoken
      }
    }
  );
};

// 顧客更新API
export const updateUser = async (
  user: AccountBasicChangeType
): Promise<void> => {
  await getCsrfToken();

  await instance.post(
    '/users',
    {
      company: user.company,
      company_kana: user.companyKana,
      company_short: user.companyShort,
      department: user.department,
      first_name: user.firstName,
      first_name_kana: user.firstNameKana,
      homepage: user.homepage,
      industry: user.industry,
      last_name: user.lastName,
      last_name_kana: user.lastNameKana,
      tel: user.tel,
      certification_flg: user.certificationFlg ?? null,
      certification_name: user.certificationName ?? null
    },
    { headers: { 'X-XSRF-TOKEN': csrfToken } }
  );
};

export type getCustomerInfoType = {
  id: string;
  email: string;
  company: string;
  company_kana: string;
  company_short: string;
  department: string;
  last_name: string;
  first_name: string;
  last_name_kana: string;
  first_name_kana: string;
  tel: string;
  homepage: string;
  industry: string;
  type: number;
  co2_status: number;
  co2_default_id: number;
  use_mylist_id: number;
  rank: number;
  certification_flg: number | null;
  certification_name: string | null;
};

// 顧客情報取得API
export const getCustomerInfo = async (): Promise<{
  data: getCustomerInfoType;
}> => {
  await getCsrfToken();

  try {
    const response = await instance.get('/users', {
      headers: {
        'X-XSRF-TOKEN': csrfToken
      }
    });
    return response;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err.response && err.response.data && err.response.data.message) {
      // サーバーからのエラーメッセージをthrowします
      throw new Error(err.response.data.message);
    } else {
      throw err;
    }
  }
};

// お問合せ送信
export const sendContact = async (
  watch: UseFormWatch<ContactType>,
  userId: string | null = null
) => {
  const csrftoken = await getCsrfTokenOnCookie();

  try {
    const response = await instance.post(
      '/contacts',
      {
        kind: contactKind.normal,
        user_id: userId,
        company: watch('company'),
        department: watch('department'),
        name: watch('name'),
        email: watch('email'),
        tel: watch('tel'),
        type: watch('type'),
        content: watch('content')
      },
      {
        headers: {
          'X-XSRF-TOKEN': csrftoken
        }
      }
    );
    return response;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err.response && err.response.data && err.response.data.message) {
      // サーバーからのエラーメッセージをthrowします
      throw new Error(err.response.data.message);
    } else {
      throw err;
    }
  }
};

// 建材登録会員の申請API
export const applyMaker = async (): Promise<void> => {
  const csrftoken = await getCsrfTokenOnCookie();

  await instance.put(
    '/users/maker_apply',
    {},
    { headers: { 'X-XSRF-TOKEN': csrftoken } }
  );
};

export const userWithdraw = async (
  reason: number,
  reasonDetail: string | undefined
): Promise<AxiosResponse> => {
  return await instance.delete(
    `/users?reason=${reason}&reason_detail=${reasonDetail}`,
    {
      headers: { 'X-XSRF-TOKEN': csrfToken }
    }
  );
};

// メールアドレス変更申請API
export const applyChangeEmail = async (email: string): Promise<void> => {
  await instance.put(
    '/users/email_change',
    { email: email },
    { headers: { 'X-XSRF-TOKEN': csrfToken } }
  );
};

// メールアドレス変更完了API
export const completeChangeEmail = async (
  confirmationToken: string
): Promise<void> => {
  await instance.put(
    '/users/email_change_verification',
    {
      token: confirmationToken
    },
    { headers: { 'X-XSRF-TOKEN': csrfToken } }
  );
};

// 建材登録会員の申請API
export const requestMakerApply = async (): Promise<AxiosResponse> => {
  return await instance.put('/users/maker_apply');
};

// 事務局 担当者一覧取得
export const getOperatorUserList = async (): Promise<AxiosResponse> => {
  return await instance.get('/operator/users_name', {
    headers: { 'X-XSRF-TOKEN': csrfToken }
  });
};

// 企業名リスト取得API
export const getCompanyList = async (): Promise<AxiosResponse> => {
  return await instance.get('/operator/users', {
    headers: { 'X-XSRF-TOKEN': csrfToken }
  });
};

// ナビパネル既読API
export const readNaviPanel = async (): Promise<void> => {
  const csrftoken = await getCsrfTokenOnCookie();
  await instance.put(
    '/users/navi/read',
    {},
    { headers: { 'X-XSRF-TOKEN': csrftoken } }
  );
};
